import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyDataTable from '../../../components/MyDataTable';
import { translate } from '../../../services/translate';
import moment from 'moment';
import { faPause, faPlay, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import CustomButton from '../../../components/CustomButton';
import Filter from './Filters';
import { InfoInRow } from '../../../components/InfoInRow';
import Badge from '../../../components/Badge';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../styles/colorPalettes';
import { AppAgentParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';

export const tags = {
    location: ['locationSent'] as DebuggerSubTag[],
    lpr: [
        'plateDetected',
        'noPlateDetected',
        'invalidLPR',
        'cachedPlateDetected',
    ] as DebuggerSubTag[],
    bodyCam: [
        'status',
        'statusChanged',
        'newOfferGenerated',
        'sendWhipOffer',
        'stopWhip',
    ] as DebuggerSubTag[],
} as const;

export const subTags = {
    locationSent: 0,
    plateDetected: 1,
    noPlateDetected: 2,
    invalidLPR: 3,
    cachedPlateDetected: 4,
    status: 5,
    statusChanged: 6,
    newOfferGenerated: 7,
    sendWhipOffer: 8,
    stopWhip: 9
} as const;

export type DebuggerTag = keyof typeof tags;
export type DebuggerSubTag = keyof typeof subTags;

interface MessageBody {
    tag: DebuggerTag;
    subTag: DebuggerSubTag;
    dateTime: string;
    extra: Record<string, string | number | undefined>;
}

// TIP: if you want to test this page run the following on the console:
// setInterval(() => {
//     // lpr
//     window.lprServiceMessage({ type: 'invalidLPR' });
//     window.lprServiceMessage({ type: 'noPlateDetected' });
//     window.lprServiceMessage({ type: 'cachedPlateDetected', plate: 'ABC1234' });
//     window.lprServiceMessage({ type: 'plateDetected', plate: 'ABC1234', confidence: '30' });

//     // location
//     window.locationMessage('20', '30');

//     // body cam
//     window.bodyCamServiceMessage({ type: 'newOfferGenerated', extra: {} });
//     window.bodyCamServiceMessage({ type: 'stopWhip', extra: {} });
//     window.bodyCamServiceMessage({ type: 'statusChanged', extra: { newState: 'CLOSED' } });
//     window.bodyCamServiceMessage({ type: 'statusChanged', extra: { newState: 'CONNECTED' } });
//     window.bodyCamServiceMessage({ type: 'statusChanged', extra: { newState: 'CONNECTING' } });
//     window.bodyCamServiceMessage({ type: 'statusChanged', extra: { newState: 'DISCONNECTED' } });
//     window.bodyCamServiceMessage({ type: 'statusChanged', extra: { newState: 'FAILED' } });
//     window.bodyCamServiceMessage({ type: 'statusChanged', extra: { newState: 'NEW' } });
//     window.bodyCamServiceMessage({ type: 'status', extra: { connectionState: 'CONNECTED', processedFrames: 6 } });
//     window.bodyCamServiceMessage({ type: 'status', extra: { connectionState: 'CLOSED' } });
//     window.bodyCamServiceMessage({ type: 'status', extra: { connectionState: 'CONNECTING' } });
//     window.bodyCamServiceMessage({ type: 'status', extra: { connectionState: 'DISCONNECTED' } });
//     window.bodyCamServiceMessage({ type: 'status', extra: { connectionState: 'FAILED' } });
//     window.bodyCamServiceMessage({ type: 'status', extra: { connectionState: 'NEW' } });
//     window.bodyCamServiceMessage({ type: 'sendWhipOffer', extra: { error: 'fatalExceptionOccurred' } });
//     window.bodyCamServiceMessage({ type: 'sendWhipOffer', extra: { attempt: 1, error: 'noWhipAvailable' } });
//     window.bodyCamServiceMessage({ type: 'sendWhipOffer', extra: { attempt: 2, error: 'badWhipAnswer' } });
//     window.bodyCamServiceMessage({ type: 'sendWhipOffer', extra: { attempt: 3 } });

// }, 2000);

export default function Debugger() {
    const route = useRoute<RouteProp<AppAgentParamList, 'Debugger'>>();

    const { styles } = useStyles(styleSheet);
    const [messages, setMessages] = useState<MessageBody[]>([]);
    const isFocused = useIsFocused();
    const [paused, setPaused] = useState<boolean>(false);
    const [filters, setFilters] = useFilters<AppAgentParamList['Debugger']>({
        ...route.params,
    });

    const addMessage = (message: MessageBody) => {
        if (!paused && (!filters.tags.length || filters.tags.includes(message.tag))) {
            setMessages(prev => {
                if (prev.length > 300) {
                    return [message];
                }
                return [message, ...prev];
            });
        }
    };

    const createMessage = (tag: DebuggerTag, msg: DebuggerSubTag, extra: Record<string, string | number | undefined>): MessageBody => ({
        tag: tag,
        subTag: msg,
        dateTime: moment().format('DD/MM/YY HH:mm:ss'),
        extra,
    });

    useFocusEffect(useCallback(() => {
        if (isFocused) {
            window.locationMessage = (latitude: string, longitude: string) => {
                addMessage(createMessage('location', 'locationSent', { latitude, longitude }));
            };

            window.lprServiceMessage = (message) => {
                if (message.type === 'invalidLPR') {
                    addMessage(createMessage('lpr', message.type, { reason: translate('agentWithoutTeam') }));
                } else if (message.type === 'cachedPlateDetected') {
                    addMessage(createMessage('lpr', message.type, { plate: message.plate, reason: translate('cachedPlateReason') }));
                } else if (message.type === 'noPlateDetected') {
                    addMessage(createMessage('lpr', message.type, {}));
                } else if (message.type === 'plateDetected') {
                    addMessage(createMessage('lpr', message.type, { plate: message.plate, confidence: message.confidence }));
                }
            };

            window.lprMessage = (logMessage: DebuggerSubTag, plate?: string, confidence?: string) => {
                if (logMessage == 'invalidLPR') {
                    addMessage(createMessage('lpr', logMessage, { reason: translate('agentWithoutTeam') }));
                } else if (logMessage == 'cachedPlateDetected') {
                    addMessage(createMessage('lpr', logMessage, { plate, reason: translate('cachedPlateReason') }));
                } else {
                    addMessage(createMessage('lpr', logMessage, plate && confidence ? { plate, confidence } : {}));
                }
            };

            window.bodyCamServiceMessage = (message) => {
                if (message.type === 'newOfferGenerated' || message.type === 'stopWhip') {
                    addMessage(createMessage('bodyCam', message.type, {}));
                } else if (message.type === 'statusChanged') {
                    addMessage(createMessage('bodyCam', message.type, { newState: translate(message.extra.newState) }));
                } else if (message.type === 'status') {
                    addMessage(createMessage('bodyCam', message.type, {
                        connectionState: translate(message.extra.connectionState),
                        sentFrames: message.extra.connectionState === 'CONNECTED' ? message.extra.processedFrames : undefined,
                    }));
                } else if (message.type === 'sendWhipOffer') {
                    addMessage(createMessage('bodyCam', message.type, {
                        information: message.extra.error ? translate(message.extra.error) : translate('successWhipOffer'),
                        attempt: message.extra.error !== 'fatalExceptionOccurred' ? message.extra.attempt : undefined,
                    }));
                }

            };
        }
    }, [isFocused, paused, filters]));

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                    <CustomButton
                        onPress={() => setPaused(!paused)}
                        icon={(paused ? faPlay : faPause) as IconDefinition}
                        type='secondary'
                    />
                    <CustomButton
                        onPress={() => setMessages([])}
                        icon={faTrash as IconDefinition}
                        type='secondary'
                    />
                </View>
                <Filter
                    filters={filters}
                    setFilters={setFilters}
                />
            </View>
            <View style={styles.card}>
                <MyDataTable
                    columns={[{
                        name: translate('messagesSentByService'),
                        cell: row => <View style={{ flex: 1, flexDirection: 'column', paddingVertical: 5 }}>
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                <InfoInRow title={'date'} value={row.dateTime} />
                                <Badge
                                    mode='ROUNDED'
                                    backgroundColor={DEFAULT_PASTEL_COLORS[subTags[row.subTag]]}
                                    size='small'
                                    textColor={DEFAULT_PASTEL_TEXT_COLORS[subTags[row.subTag]]}
                                >
                                    {translate(row.subTag)}
                                </Badge>
                            </View>
                            <InfoInRow title={'message'} value={Object.keys(row.extra).length > 0 ? ' ' : undefined} />
                            <View style={{ paddingLeft: 10 }}>
                                {Object.entries(row.extra)
                                    .filter(([, value]) => value !== undefined)
                                    .map(([key, value]) => (
                                        <InfoInRow key={key} title={key} value={value} />
                                    ))}
                            </View>
                        </View>,
                        wrap: true,
                    }]}
                    noResultsText={translate('waitingForServices')}
                    data={messages.filter(m => {
                        if (!filters.tags.length) {
                            return true;
                        }

                        if (!filters.tags.includes(m.tag)) {
                            return false;
                        }

                        if (!filters.subTags.length) {
                            return true;
                        }

                        return filters.subTags.includes(m.subTag);
                    })}
                />
            </View>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        rowGap: 10
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 10,
        zIndex: 2,
    },
    card: {
        borderWidth: 1,
        borderRadius: 8,
        borderColor: theme.colors.card.border,
        padding: 8,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: theme.colors.card.background,
        height: 80,
        flex: 1
    }
}));
